import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Route, Switch } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch as SwitchToggle } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ListFilter, Search, Plus, Pencil, Trash2, ArrowLeft, Loader2 } from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import { useToast } from "@/hooks/use-toast";

// --- Types ---

interface TaxSetting {
  id: number;
  tax_type: string;
  rate: number;
  applies_to: string[];
  jurisdiction: string;
  effective_date: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// --- Constants ---

const TAX_TYPES = [
  { value: "sales_tax", label: "Sales Tax" },
  { value: "gratuity", label: "Gratuity" },
  { value: "vat", label: "VAT" },
  { value: "excise", label: "Excise" },
  { value: "service_charge", label: "Service Charge" },
  { value: "custom", label: "Custom" },
];

const APPLIES_TO_OPTIONS = [
  { value: "membership", label: "Membership" },
  { value: "entry_pass", label: "Entry Pass" },
  { value: "order", label: "Order" },
];

const formatTaxType = (type: string) => {
  const found = TAX_TYPES.find((t) => t.value === type);
  return found?.label ?? type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatAppliesTo = (label: string) =>
  label.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// --- Page state ---

type TaxSettingsState = {
  searchQuery?: string;
  currentPage?: number;
  itemsPerPage?: number;
  status?: string;
  dateRange?: { from: string; to: string };
};

const defaultState: TaxSettingsState = {
  searchQuery: "",
  currentPage: 1,
  itemsPerPage: 10,
  status: "",
};

// --- Fetch helper ---

const fetchTaxSettings = async (): Promise<TaxSetting[]> => {
  // Admin panel authenticates with the POS session cookie, not Firebase.
  // The /v1/taxes/settings CRUD endpoints are Firebase-gated (authenticateFirebaseToken),
  // so calling them with the POS cookie returns 401 → pos-session-expired → the admin
  // layout bounces to /admin/login, which then redirects back to /admin since the POS
  // session is still valid. Use the POS-admin namespace like the other platform pages
  // (/v1/pos/admin/waivers, /v1/pos/admin/users) instead.
  const res = await posApiFetch("/v1/pos/admin/taxes/settings");
  if (!res.ok) throw new Error("Failed to fetch tax settings");
  const json = await res.json();
  return Array.isArray(json) ? json : json?.data ?? [];
};

// --- Helpers ---

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

// ============================================================
// List Page
// ============================================================

function TaxSettingsListPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = "Tax Settings | SECH Admin";
  }, []);

  const [pageState, setPageState] = usePagePersistentState<TaxSettingsState>("taxSettings", defaultState);
  const searchQuery = pageState.searchQuery;
  const currentPage = pageState.currentPage;
  const itemsPerPage = pageState.itemsPerPage;
  const statusFilter = pageState.status;
  const dateRange = pageState.dateRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TaxSetting | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "tax_settings_table",
    defaultColumn: "created_at",
    defaultDirection: "desc",
  });

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    status: "",
    dateRange: { from: "", to: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      status: statusFilter ?? "",
      dateRange: { from: dateRange?.from ?? "", to: dateRange?.to ?? "" },
    }));
  }, [statusFilter, dateRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setPageState((prev) => ({
      ...prev,
      status: values.status,
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
    }));
  };

  const { data: taxSettings = [], isLoading, error } = useQuery<TaxSetting[]>({
    queryKey: ["/v1/taxes/settings"],
    queryFn: fetchTaxSettings,
    retry: 3,
    retryDelay: 1000,
  });

  const getFilterCount = () => {
    let count = 0;
    if (pageState.status && pageState.status !== "" && pageState.status !== "all") count += 1;
    const fromDate = normalizeDate(pageState.dateRange?.from);
    const toDate = normalizeDate(pageState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    return count;
  };

  const taxFilters: FilterConfig[] = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: statusOptions, placeholder: "Select Status" },
    { key: "dateRange", label: "Date Range", type: "dateRange" },
  ], []);

  const handleClear = () => {
    setFilterValues({ status: "", dateRange: { from: "", to: "" } });
    setPageState((prev) => ({ ...prev, status: "", dateRange: { from: "", to: "" }, currentPage: 1 }));
  };

  const handleDeactivate = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await posApiFetch(`/v1/pos/admin/taxes/settings/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to deactivate tax setting");
      toast({ title: "Tax setting deactivated successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/taxes/settings"] });
    } catch (err: any) {
      toast({ title: "Failed to deactivate tax setting", description: err?.message || "Unknown error", variant: "destructive" });
    } finally {
      setIsDeleting(false);
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const tableColumns = useMemo(() => [
    {
      label: "Tax Type", field: "tax_type", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[#505258] font-medium">{formatTaxType(value)}</span>
      ),
    },
    {
      label: "Rate", field: "rate", sortable: true,
      cellTemplate: (value: number) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">
          {(value * 100).toFixed(2)}%
        </span>
      ),
    },
    {
      label: "Applies To", field: "applies_to",
      cellTemplate: (value: string[] | string) => (
        <div className="flex flex-wrap gap-1">
          {(Array.isArray(value) ? value : []).map((item) => (
            <span key={item} className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700">
              {formatAppliesTo(item)}
            </span>
          ))}
        </div>
      ),
    },
    {
      label: "Jurisdiction", field: "jurisdiction", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "-"}</span>
      ),
    },
    {
      label: "Effective Date", field: "effective_date", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">
          {value ? new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "-"}
        </span>
      ),
    },
    {
      label: "Status", field: "is_active", sortable: true,
      cellTemplate: (value: boolean) => {
        const status = value ? "active" : "inactive";
        return (
          <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColorCode(status)}`}>
            <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(status)} inline-flex items-center justify-center`} />
            <span className="leading-5 text-[13px] font-normal">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </span>
        );
      },
    },
    {
      label: "Created", field: "created_at", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: TaxSetting) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: TaxSetting) => navigate(`/admin/tax-settings/${r.id}/edit`) },
            { label: "Deactivate", icon: Trash2, onClick: (r: TaxSetting) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], []);

  const filteredSettings = useMemo(() =>
    taxSettings.filter((setting) => {
      const search = debouncedSearch.toLowerCase();
      const matchesSearch = !search
        || setting.tax_type?.toLowerCase().includes(search)
        || formatTaxType(setting.tax_type).toLowerCase().includes(search)
        || setting.jurisdiction?.toLowerCase().includes(search)
        || (Array.isArray(setting.applies_to) && setting.applies_to.some((a) => a.toLowerCase().includes(search)));
      const settingStatus = setting.is_active ? "active" : "inactive";
      const matchesStatus = !statusFilter || statusFilter === "" || statusFilter === "all" || settingStatus === statusFilter;
      const fromDate = normalizeDate(pageState.dateRange?.from);
      const toDate = normalizeDate(pageState.dateRange?.to);
      const createdAt = new Date(setting.created_at).getTime();
      const matchesDateRange = fromDate && toDate ? createdAt >= fromDate.getTime() && createdAt <= toDate.getTime() : true;
      return matchesSearch && matchesStatus && matchesDateRange;
    }), [taxSettings, debouncedSearch, statusFilter, pageState.dateRange]
  );

  const sortedSettings = useMemo(() => {
    return [...filteredSettings].sort((a: TaxSetting, b: TaxSetting) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "tax_type": aVal = a.tax_type ?? ""; bVal = b.tax_type ?? ""; break;
        case "rate": aVal = a.rate ?? 0; bVal = b.rate ?? 0; break;
        case "jurisdiction": aVal = a.jurisdiction ?? ""; bVal = b.jurisdiction ?? ""; break;
        case "effective_date":
          aVal = a.effective_date ? new Date(a.effective_date).getTime() : 0;
          bVal = b.effective_date ? new Date(b.effective_date).getTime() : 0;
          break;
        case "is_active": aVal = a.is_active ? 1 : 0; bVal = b.is_active ? 1 : 0; break;
        case "created_at":
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredSettings, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedSettings.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedSettings = sortedSettings.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setPageState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setPageState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedSettings.length, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Tax Settings</h2>
          <p className="text-gray-600 mb-4">Failed to load tax settings</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Tax Settings</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setPageState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
          <Button onClick={() => navigate("/admin/tax-settings/add")} className="h-[32px] px-3 text-[13px] font-medium bg-[var(--primary-color)] text-white rounded-[4px] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Tax
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-3 h-[calc(100vh-130px)]">
          {!paginatedSettings.length && !isLoading ? (
            <NoRecordFound title="Tax Settings" description="There are no tax settings configured." />
          ) : (
            <CommonGrid
              rowData={paginatedSettings}
              isLoading={isLoading}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>

      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={taxFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <ConfirmModal open={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} onConfirm={handleDeactivate} title="Deactivate Tax Setting" description="Are you sure you want to deactivate this tax setting? It will no longer be applied to new transactions." confirmText={isDeleting ? "Deactivating..." : "Deactivate"} variant="delete" />
    </div>
  );
}

// ============================================================
// Editor (Add / Edit)
// ============================================================

interface TaxSettingFormState {
  tax_type: string;
  rate: string;
  applies_to: string[];
  jurisdiction: string;
  effective_date: string;
  is_active: boolean;
}

const EMPTY_FORM: TaxSettingFormState = {
  tax_type: "",
  rate: "",
  applies_to: [],
  jurisdiction: "",
  effective_date: "",
  is_active: true,
};

function TaxSettingEditor({ settingId }: { settingId?: string }) {
  const isEdit = !!settingId;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<TaxSettingFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    document.title = isEdit ? "Edit Tax Setting | SECH Admin" : "Add Tax Setting | SECH Admin";
  }, [isEdit]);

  const { data: existingSetting, isLoading } = useQuery<TaxSetting>({
    queryKey: ["/v1/taxes/settings", settingId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/admin/taxes/settings/${settingId}`);
      if (!res.ok) throw new Error("Failed to fetch tax setting");
      return res.json();
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existingSetting) return;
    const s = existingSetting;
    setForm({
      tax_type: s.tax_type ?? "",
      rate: s.rate != null ? String((s.rate * 100).toFixed(2)) : "",
      applies_to: s.applies_to ?? [],
      jurisdiction: s.jurisdiction ?? "",
      effective_date: s.effective_date ? s.effective_date.slice(0, 10) : "",
      is_active: s.is_active ?? true,
    });
  }, [existingSetting]);

  const set = <K extends keyof TaxSettingFormState>(key: K, value: TaxSettingFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleAppliesTo = (value: string) => {
    setForm((prev) => {
      const current = prev.applies_to;
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, applies_to: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.tax_type) {
      toast({ title: "Validation Error", description: "Tax type is required", variant: "destructive" });
      return;
    }
    const rateNum = parseFloat(form.rate);
    if (isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
      toast({ title: "Validation Error", description: "Rate must be between 0 and 100", variant: "destructive" });
      return;
    }
    if (form.applies_to.length === 0) {
      toast({ title: "Validation Error", description: "At least one 'Applies To' option is required", variant: "destructive" });
      return;
    }
    if (!form.jurisdiction.trim()) {
      toast({ title: "Validation Error", description: "Jurisdiction is required", variant: "destructive" });
      return;
    }
    if (!form.effective_date) {
      toast({ title: "Validation Error", description: "Effective date is required", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const body = {
        tax_type: form.tax_type,
        rate: rateNum / 100,
        applies_to: form.applies_to,
        jurisdiction: form.jurisdiction.trim(),
        effective_date: form.effective_date,
        is_active: form.is_active,
      };

      const url = isEdit ? `/v1/pos/admin/taxes/settings/${settingId}` : "/v1/pos/admin/taxes/settings";
      const method = isEdit ? "PUT" : "POST";

      const res = await posApiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} tax setting`);
      }

      toast({ title: `Tax setting ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/taxes/settings"] });
      navigate("/admin/tax-settings");
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Failed to save tax setting", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isEdit && isLoading) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={() => navigate("/admin/tax-settings")} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Tax Settings
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={() => navigate("/admin/tax-settings")} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Tax Settings
        </Button>
        <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
          {isEdit ? "Edit Tax Setting" : "Create Tax Setting"}
        </h2>
        <div className="w-[140px]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Tax Details</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Tax Type *</Label>
                <Select value={form.tax_type} onValueChange={(v) => set("tax_type", v)}>
                  <SelectTrigger className="h-[36px] text-[13px]">
                    <SelectValue placeholder="Select tax type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} className="text-[13px]">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Rate (%) *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.rate}
                  onChange={(e) => set("rate", e.target.value)}
                  placeholder="e.g. 7.50"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Jurisdiction *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.jurisdiction}
                  onChange={(e) => set("jurisdiction", e.target.value)}
                  placeholder="e.g. Florida, Service Charge"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Effective Date *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => set("effective_date", e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Applies To *</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex flex-wrap gap-4">
              {APPLIES_TO_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.applies_to.includes(opt.value)}
                    onCheckedChange={() => toggleAppliesTo(opt.value)}
                  />
                  <span className="text-[13px]">{opt.label}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Settings</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex items-center gap-2">
              <SwitchToggle checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} />
              <Label className="text-[12px]">Active</Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className="h-[36px] text-[13px]" onClick={() => navigate("/admin/tax-settings")} disabled={isSaving}>Cancel</Button>
          <Button type="submit" className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]" disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update Tax Setting" : "Create Tax Setting"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Root Router
// ============================================================

export default function TaxSettingsPage() {
  return (
    <Switch>
      <Route path="/admin/tax-settings/add">
        <TaxSettingEditor />
      </Route>
      <Route path="/admin/tax-settings/:id/edit">
        {(params) => <TaxSettingEditor settingId={params.id} />}
      </Route>
      <Route path="/admin/tax-settings" component={TaxSettingsListPage} />
    </Switch>
  );
}
